import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import type {
  CustomSortOrder,
  ExcelImportResponse,
  InviteSurveyRequest,
  SurveyAiReviewReportDto,
  SurveyParticipationBasicDtoPagedResponseDto,
  SurveyParticipationExcelImportRequest,
  SurveyParticipationSortColumn,
  SurveyParticipationStatus,
  SurveyQuestionResponse,
  SurveyResponse,
  UpdateSurveyQuestionRequest,
  UpdateSurveyTitleRequest,
} from '@/services/api';
import { SurveyService } from '@/services/api';

export const adminSurveyKeys = {
  getSurvey: (params: { surveyId: string }) => ['survey', params] as const,
  getSurveyQuestions: (params: { surveyId: string }) =>
    ['survey-questions', params] as const,
  getSurveyParticipants: (params: GetSurveyParticipantsParams) =>
    ['survey-participants', params] as const,
  getSurveyReports: (params: { surveyId: string }) =>
    ['survey-reports', params] as const,
  getSurveyReportsHeatmap: (params: { surveyId: string }) =>
    ['survey-reports-heatmap', params] as const,
  getSurveyReportsDimensionQuestionDetail: (params: { surveyId: string }) =>
    ['survey-reports-dimension-question-detail', params] as const,
  getSurveyReportsAiReview: (params: { surveyId: string }) =>
    ['survey-reports-ai-review', params] as const,
};

type GetSurveyQueryParams = {
  surveyId: string;
};

export const useGetSurveyQuery = (
  params: GetSurveyQueryParams,
  options?: Partial<UseQueryOptions<SurveyResponse>>
) => {
  return useQuery({
    queryKey: adminSurveyKeys.getSurvey(params),
    queryFn: async () => {
      return await SurveyService.getAllSurveys({
        surveyId: params.surveyId,
      });
    },
    enabled: !!params.surveyId,
    ...options,
  });
};

type GetSurveyAiReviewsQueryParams = {
  surveyId: string;
};

export const useGetSurveyAiReviewsQuery = (
  params: GetSurveyAiReviewsQueryParams,
  options?: Partial<UseQueryOptions<SurveyAiReviewReportDto>>
) => {
  return useQuery({
    queryKey: adminSurveyKeys.getSurveyReportsAiReview(params),
    queryFn: async () => {
      return await SurveyService.getAllSurveysReportsAiReview(params);
    },
    enabled: !!params.surveyId,
    ...options,
  });
};

type GetAllSurveyQuestionsQueryParams = {
  surveyId: string;
};

export const useGetAllSurveyQuestionsQuery = (
  params: GetAllSurveyQuestionsQueryParams,
  options?: Partial<UseQueryOptions<SurveyQuestionResponse>>
) => {
  return useQuery({
    queryKey: adminSurveyKeys.getSurveyQuestions(params),
    queryFn: async () => {
      return await SurveyService.getAllSurveysQuestions({
        surveyId: params.surveyId,
      });
    },
    enabled: !!params.surveyId,
    ...options,
  });
};

type AddSurveyParticipantExcelParams = {
  surveyId: string;
  body: SurveyParticipationExcelImportRequest;
};

export const useAddSurveyParticipantExcelMutation = (
  options?: Partial<
    UseMutationOptions<
      ExcelImportResponse,
      AxiosError,
      AddSurveyParticipantExcelParams
    >
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    ExcelImportResponse,
    AxiosError,
    AddSurveyParticipantExcelParams
  >({
    mutationFn: (params) => SurveyService.postAllSurveysInviteExcel(params),
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: adminSurveyKeys.getSurvey({ surveyId: variables.surveyId }),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

type UpdateSurveyMutationParams = {
  surveyId: string;
  body: UpdateSurveyTitleRequest;
};

export const useUpdateSurveyMutation = (
  options?: UseMutationOptions<string, AxiosError, UpdateSurveyMutationParams>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateSurveyMutationParams) => {
      return await SurveyService.putAllSurveys({
        surveyId: params.surveyId,
        body: params.body,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminSurveyKeys.getSurvey({ surveyId: variables.surveyId }),
      });
    },
    ...options,
  });
};

export type GetSurveyParticipantsParams = {
  surveyId: string;
  q?: string;
  status?: Array<SurveyParticipationStatus>;
  createdAtFrom?: string;
  createdAtTo?: string;
  completedAtFrom?: string;
  completedAtTo?: string;
  sortColumn?: SurveyParticipationSortColumn;
  sortOrder?: CustomSortOrder;
  size?: number;
  page?: number;
};

type InviteSurveyParticipantParams = {
  surveyId: string;
  body: InviteSurveyRequest;
};

export const useInviteSurveyParticipantMutation = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, InviteSurveyParticipantParams>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, InviteSurveyParticipantParams>({
    mutationFn: (params) => SurveyService.postAllSurveysInvite(params),
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: adminSurveyKeys.getSurveyParticipants({
          surveyId: variables.surveyId,
        }),
      });
      await queryClient.invalidateQueries({
        queryKey: adminSurveyKeys.getSurvey({ surveyId: variables.surveyId }),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useGetSurveyParticipantsQuery = (
  params: GetSurveyParticipantsParams,
  options?: Partial<
    UseQueryOptions<SurveyParticipationBasicDtoPagedResponseDto, AxiosError>
  >
) => {
  return useQuery({
    queryKey: adminSurveyKeys.getSurveyParticipants(params),
    queryFn: async () => {
      return await SurveyService.getAllSurveysParticipants({
        surveyId: params.surveyId,
        q: params.q,
        status: params.status,
        createdAtFrom: params.createdAtFrom,
        createdAtTo: params.createdAtTo,
        completedAtFrom: params.completedAtFrom,
        completedAtTo: params.completedAtTo,
        sortColumn: params.sortColumn,
        sortOrder: params.sortOrder,
        size: params.size,
        page: params.page,
      });
    },
    enabled: !!params.surveyId,
    ...options,
  });
};

type DeleteSurveyParticipantsParams = {
  surveyId: string;
  participationIds: Array<string>;
};

export const useDeleteSurveyParticipantsMutation = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, DeleteSurveyParticipantsParams>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, DeleteSurveyParticipantsParams>({
    mutationFn: (params) =>
      SurveyService.deleteSurveysParticipantsById({
        surveyId: params.surveyId,
        participationIds: params.participationIds,
      }),
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: adminSurveyKeys.getSurveyParticipants({
          surveyId: variables.surveyId,
        }),
      });
      await queryClient.invalidateQueries({
        queryKey: adminSurveyKeys.getSurvey({ surveyId: variables.surveyId }),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export type UpdateSurveyQuestionsParams = {
  surveyId: string;
  body: UpdateSurveyQuestionRequest;
};

export const useUpdateSurveyQuestionsMutation = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, UpdateSurveyQuestionsParams>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, UpdateSurveyQuestionsParams>({
    mutationFn: (params) => SurveyService.putAllSurveysQuestions(params),
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: adminSurveyKeys.getSurveyQuestions({
          surveyId: variables.surveyId,
        }),
      });
      await queryClient.invalidateQueries({
        queryKey: adminSurveyKeys.getSurvey({ surveyId: variables.surveyId }),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export type InviteSurveyParticipantExcelParams = {
  surveyId: string;
  body: SurveyParticipationExcelImportRequest;
};

export const useInviteSurveyParticipantExcelMutation = (
  options?: Partial<
    UseMutationOptions<
      ExcelImportResponse,
      AxiosError,
      InviteSurveyParticipantExcelParams
    >
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    ExcelImportResponse,
    AxiosError,
    InviteSurveyParticipantExcelParams
  >({
    mutationFn: (params) => SurveyService.postAllSurveysInviteExcel(params),
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: adminSurveyKeys.getSurveyQuestions({
          surveyId: variables.surveyId,
        }),
      });
      await queryClient.invalidateQueries({
        queryKey: adminSurveyKeys.getSurvey({ surveyId: variables.surveyId }),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

type SendToSelectedParticipantsParams = {
  surveyId: string;
  participationIds: Array<string>;
};

export const useSendToSelectedParticipantsMutation = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, SendToSelectedParticipantsParams>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: SendToSelectedParticipantsParams) => {
      return await SurveyService.putAllSurveysParticipantsReinvite({
        surveyId: params.surveyId,
        body: {
          participationIds: params.participationIds,
        },
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminSurveyKeys.getSurveyParticipants({
          surveyId: variables.surveyId,
        }),
      });
    },
    ...options,
  });
};
